import * as React from "react";

import { Label } from "@impero-com/common/components/html";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { DeleteButton } from "@impero-com/ui";
import { Cascader } from "antd";
import type { DefaultOptionType } from "antd/es/cascader";

import * as theme from "./theme.css";

class DataViewerGroupingSelector extends React.Component<DataViewerGroupingSelectorProps> {
    render() {
        const { id, hideTitle, groupingExtractorIds, extractorGroups } =
            this.props;

        const nextExtractorId = this.getNextExtractorId();
        const options: Array<DefaultOptionType> = extractorGroups.map(
            (group) => ({
                value: group.id,
                label: group.label,
                children: group.extractors.map((ext) => ({
                    value: ext.id,
                    label: ext.label,
                })),
            })
        );

        return (
            <div>
                {hideTitle ? null : (
                    <div className={theme.groupingTitle}>
                        <Label>
                            {t("DataViewer_GroupingSelector_Grouping")}
                        </Label>
                    </div>
                )}
                {groupingExtractorIds
                    ? groupingExtractorIds.map((extId, index) => (
                          <div key={index} className={theme.row}>
                              <Cascader
                                  id={`${id}-${index}`}
                                  className={theme.cascader}
                                  options={options}
                                  value={this.selectedPath(extId)}
                                  showSearch
                                  displayRender={renderSelectedLabel}
                                  onChange={(value) => {
                                      const extractorId =
                                          value?.[value.length - 1];
                                      if (typeof extractorId === "string") {
                                          this.selectGroupingExtractor(
                                              index,
                                              extractorId
                                          );
                                      }
                                  }}
                              />
                              <DeleteButton
                                  className={theme.deleteGroupingButton}
                                  onClick={() =>
                                      this.deleteGroupingExtractor(index)
                                  }
                              />
                          </div>
                      ))
                    : null}
                {nextExtractorId ? (
                    <div className={theme.addRow}>
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                this.addGroupingExtractor(nextExtractorId);
                            }}
                        >
                            {t("DataViewer_GroupingSelector_Add")}
                        </a>
                    </div>
                ) : null}
            </div>
        );
    }
    // Cascader path [groupId, extractorId] for the selected extractor.
    private selectedPath(selectedExtractorId: string): Array<string> {
        const group = this.props.extractorGroups.find((group) =>
            group.extractors.some((ext) => ext.id === selectedExtractorId)
        );
        return group ? [group.id, selectedExtractorId] : [];
    }
    private getNextExtractorId() {
        const { extractorGroups, groupingExtractorIds } = this.props;
        if (!groupingExtractorIds) {
            return extractorGroups[0].id;
        }
        const hash = Object.fromEntries(
            groupingExtractorIds.map((id) => [id, id])
        );
        for (const group of extractorGroups) {
            for (const { id } of group.extractors) {
                if (!hash[id]) {
                    return id;
                }
            }
        }
        return undefined;
    }
    private selectGroupingExtractor(
        groupingIndex: number,
        extractorId: string
    ) {
        const { groupingExtractorIds } = this.props;
        if (
            !groupingExtractorIds ||
            groupingExtractorIds[groupingIndex] === extractorId
        ) {
            return;
        }
        const newGroupingExtractorIds: Array<string> = [];
        groupingExtractorIds.forEach((extId, index) => {
            if (index === groupingIndex) {
                newGroupingExtractorIds.push(extractorId);
            } else if (extId !== extractorId) {
                newGroupingExtractorIds.push(extId);
            } // else remove the row
        });
        this.props.onChange(newGroupingExtractorIds);
    }
    private addGroupingExtractor(extractorId: string) {
        const { groupingExtractorIds } = this.props;
        const newGroupingExtractorIds = (groupingExtractorIds || []).concat([
            extractorId,
        ]);
        this.props.onChange(newGroupingExtractorIds);
    }
    private deleteGroupingExtractor(groupingIndex: number) {
        const { groupingExtractorIds } = this.props;
        const newGroupingExtractorIds: Array<string> = groupingExtractorIds
            ? groupingExtractorIds.slice()
            : [];
        newGroupingExtractorIds.splice(groupingIndex, 1);
        this.props.onChange(newGroupingExtractorIds);
    }
}

// Selected Cascader path rendered as "Group: item", group part muted.
function renderSelectedLabel(labels: Array<string>) {
    if (labels.length < 2) {
        return labels[labels.length - 1] ?? "";
    }
    return (
        <span>
            <span className={theme.selectedGroup}>{labels[0]}:</span>{" "}
            {labels[1]}
        </span>
    );
}

interface DataViewerGroupingSelectorProps {
    id: string;
    hideTitle?: boolean;
    extractorGroups: Array<DV.DataExtractorGroup<any>>;
    groupingExtractorIds?: Array<string>;
    // Events
    onChange: (extractorIds: Array<string>) => void;
}

export { DataViewerGroupingSelector };
